import { useMemo, useState } from 'react';
import { TabsContent } from '@/components/ui/tabs';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { RefreshCw, ShieldCheck, ShieldAlert, ShieldQuestion } from 'lucide-react';
import { formatCNPJ } from '@/lib/utils/formatters';
import { useListaContribuintes } from '@/services/Contribuintes/hooks/useListaContribuintes';
import {
  useIntegracaoRTCStatus,
  useSalvarIntegracaoRTC,
  useTestarConexaoIntegracaoRTC,
} from '@/services/Integracoes';

const statusConfig: Record<string, { label: string; className: string; icon: typeof ShieldCheck }> =
  {
    Conectado: {
      label: 'Conectado',
      className: 'bg-green-500 text-white hover:bg-green-500',
      icon: ShieldCheck,
    },
    Erro: {
      label: 'Erro na sincronização',
      className: 'bg-red-500 text-white hover:bg-red-500',
      icon: ShieldAlert,
    },
  };

export function IntegracoesTab() {
  const { data: contribuintes, isLoading: isLoadingContribuintes } = useListaContribuintes();
  const [cnpjSelecionado, setCnpjSelecionado] = useState('');
  const [clientId, setClientId] = useState('');
  const [clientSecret, setClientSecret] = useState('');

  const contribuintesOrdenados = useMemo(
    () => [...(contribuintes ?? [])].sort((a, b) => a.Nome.localeCompare(b.Nome)),
    [contribuintes]
  );

  const { data: statusResponse, isLoading: isLoadingStatus } =
    useIntegracaoRTCStatus(cnpjSelecionado);
  const status = statusResponse?.data;

  const salvarMutation = useSalvarIntegracaoRTC();
  const testarMutation = useTestarConexaoIntegracaoRTC();

  const handleSalvar = () => {
    if (!cnpjSelecionado || !clientId || !clientSecret) return;
    salvarMutation.mutate(
      { CNPJ_Contribuinte: cnpjSelecionado, ClientId: clientId, ClientSecret: clientSecret },
      {
        // Só limpa o campo quando o salvamento deu certo de verdade. `onSuccess` do
        // react-query só diz que o HTTP foi 200, e esta API responde 200 também quando
        // a regra de negócio falha — limpar aí faz o operador perder o segredo digitado
        // junto com a mensagem de erro, e concluir que "não salvou de novo".
        onSuccess: response => {
          if (response.status.resultado) setClientSecret('');
        },
      }
    );
  };

  const badge = status?.StatusUltimaSincronizacao
    ? statusConfig[status.StatusUltimaSincronizacao]
    : null;

  return (
    <TabsContent value="integracoes" className="space-y-4">
      <Card>
        <CardHeader>
          <CardTitle className="text-base">API de Apuração da CBS (RTC)</CardTitle>
          <p className="text-sm text-muted-foreground">
            Cadastre o Client ID e Client Secret gerados no Portal RTC (gov.br → Gerar Credencial
            para API) para habilitar a consulta de débitos CBS na Apuração Detalhada.
          </p>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="space-y-2 max-w-md">
            <label className="text-xs text-muted-foreground font-medium">Empresa</label>
            <Select
              value={cnpjSelecionado}
              onValueChange={setCnpjSelecionado}
              disabled={isLoadingContribuintes}
            >
              <SelectTrigger>
                <SelectValue placeholder="Selecione a empresa" />
              </SelectTrigger>
              <SelectContent>
                {contribuintesOrdenados.map(contribuinte => (
                  <SelectItem
                    key={contribuinte.CNPJ_Contribuinte}
                    value={contribuinte.CNPJ_Contribuinte}
                  >
                    {contribuinte.Nome} - {formatCNPJ(contribuinte.CNPJ_Contribuinte)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {!cnpjSelecionado ? (
            <p className="text-sm text-muted-foreground">
              Selecione uma empresa para ver ou cadastrar as credenciais RTC.
            </p>
          ) : isLoadingStatus ? (
            <Skeleton className="h-24 w-full max-w-md" />
          ) : (
            <>
              <div className="flex items-center gap-2">
                {status?.Configurado ? (
                  badge ? (
                    <Badge className={badge.className}>
                      <badge.icon className="h-3.5 w-3.5 mr-1" />
                      {badge.label}
                    </Badge>
                  ) : (
                    <Badge variant="outline" className="gap-1">
                      <ShieldQuestion className="h-3.5 w-3.5" />
                      Configurado - conexão ainda não testada
                    </Badge>
                  )
                ) : (
                  <Badge variant="outline" className="text-muted-foreground">
                    Não configurado
                  </Badge>
                )}
                {status?.Configurado && (
                  <Button
                    variant="outline"
                    size="sm"
                    className="gap-2"
                    disabled={testarMutation.isPending}
                    onClick={() => testarMutation.mutate(cnpjSelecionado)}
                  >
                    <RefreshCw
                      className={`h-3.5 w-3.5 ${testarMutation.isPending ? 'animate-spin' : ''}`}
                    />
                    Testar Conexão
                  </Button>
                )}
              </div>

              {status?.MensagemUltimaSincronizacao && (
                <p className="text-xs text-muted-foreground">
                  {status.MensagemUltimaSincronizacao}
                </p>
              )}

              <div className="grid gap-4 max-w-md">
                <div className="space-y-2">
                  <label
                    htmlFor="rtc-client-id"
                    className="text-xs text-muted-foreground font-medium"
                  >
                    Client ID
                  </label>
                  <Input
                    id="rtc-client-id"
                    value={clientId}
                    onChange={e => setClientId(e.target.value)}
                    placeholder={status?.ClientId || 'Client ID gerado no Portal RTC'}
                  />
                </div>
                <div className="space-y-2">
                  <label
                    htmlFor="rtc-client-secret"
                    className="text-xs text-muted-foreground font-medium"
                  >
                    Client Secret
                  </label>
                  <Input
                    id="rtc-client-secret"
                    type="password"
                    value={clientSecret}
                    onChange={e => setClientSecret(e.target.value)}
                    placeholder={
                      status?.Configurado ? '••••••••••••' : 'Client Secret gerado no Portal RTC'
                    }
                  />
                </div>
                <Button
                  className="w-fit"
                  disabled={!clientId || !clientSecret || salvarMutation.isPending}
                  onClick={handleSalvar}
                >
                  {status?.Configurado ? 'Substituir Credenciais' : 'Salvar Credenciais'}
                </Button>
              </div>
            </>
          )}
        </CardContent>
      </Card>
    </TabsContent>
  );
}
